/**
 * Générateur — Automatisme : les unités d'aire (cm², dm², m²)
 * Attendu ID : 1026 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève sait que 1 cm² est
 * l'aire d'un carré de 1 cm de côté, que 1 m² est l'aire d'un carré de 1 m
 * de côté, que 1 dm² est l'aire d'un carré de 1 dm de côté. »
 * Labels (cartes exerciseur) :
 *   - Les unités d'aire (cm², dm², m²)
 *
 * ── Plan de cartes (2026-08-21, validé prof) ───────────────────────────────
 * UNE seule chose à installer : l'unité d'aire EST un carré, et son côté
 * porte le nom de l'unité de longueur. TROIS formats (le « choix d'unité
 * adaptée » de la flash est ÉCARTÉ : c'est un autre automatisme) :
 *   - QCM, 3 sous-types : définition directe (« l'aire d'un carré de 1 dm
 *     de côté ? » → 1 dm²), définition INVERSE (« 1 cm², c'est l'aire de
 *     quel carré ? »), et LECTURE de la notation (« comment lit-on dm² ? ») ;
 *   - V/F sur un pool fermé étiqueté à la main ;
 *   - DRAG : la phrase-lien rituelle à trous (tuiles cm², dm², m² —
 *     la saisie est écartée : taper « ² » est un piège d'interface).
 * LE distracteur transversal : « 1 dm » SANS le carré (la confusion
 * longueur / aire), toujours disponible.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 1026;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Les unités d\'aire (cm², dm², m²)';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1026 : ${message}`);
};

/** Les trois unités du domaine (fermé). */
const UNITES = [
    { aire: 'cm²', lon: 'cm', nom: 'centimètre carré' },
    { aire: 'dm²', lon: 'dm', nom: 'décimètre carré' },
    { aire: 'm²', lon: 'm', nom: 'mètre carré' },
];

// ─── Format 1 : QCM (définition directe, inverse, lecture) ────────────────

const genQcm = () => {
    const u = randomChoice(UNITES);
    const autres = UNITES.filter((x) => x !== u);
    const sousType = randomChoice(['directe', 'inverse', 'lecture']);
    let texte;
    let bonne;
    let dis;
    if (sousType === 'directe') {
        texte = randomChoice([
            `<p><b>Quelle est l'aire d'un carré de 1 ${u.lon} de côté ?</b></p>`,
            `<p><b>Un carré de 1 ${u.lon} de côté a une aire de...</b></p>`,
        ]);
        bonne = `1 ${u.aire}`;
        dis = [
            { valeur: `1 ${autres[0].aire}`, code: 'unite_voisine' },
            { valeur: `1 ${autres[1].aire}`, code: 'unite_voisine' },
            { valeur: `1 ${u.lon}`, code: 'sans_carre' },
        ];
    } else if (sousType === 'inverse') {
        texte = randomChoice([
            `<p><b>1 ${u.aire}, c'est l'aire de quel carré ?</b></p>`,
            `<p><b>Quel carré a une aire de 1 ${u.aire} ?</b></p>`,
        ]);
        bonne = `D'un carré de 1 ${u.lon} de côté`;
        dis = [
            { valeur: `D'un carré de 2 ${u.lon} de côté`, code: 'carre_double' },
            { valeur: `D'un carré de 1 ${randomChoice(autres).lon} de côté`, code: 'unite_voisine' },
            { valeur: `D'un rectangle de 1 ${u.lon} sur 2 ${u.lon}`, code: 'pas_un_carre' },
        ];
    } else {
        texte = `<p><b>Comment lit-on l'écriture « ${u.aire} » ?</b></p>`;
        bonne = u.nom;
        const racine = u.nom.split(' ')[0];
        dis = [
            { valeur: `${racine} deux`, code: 'lecture_chiffre' },
            { valeur: `deux ${racine}s`, code: 'lecture_chiffre' },
            // « double décimètre » (l'instrument) n'existe que pour dm ;
            // pour cm et m, le 3e piège est l'unité voisine lue correctement
            u.lon === 'dm'
                ? { valeur: 'double décimètre', code: 'confusion_outil' }
                : { valeur: randomChoice(autres).nom, code: 'unite_voisine' },
        ];
    }
    const vus = new Set([bonne]);
    const disOk = dis.filter((c) => {
        if (vus.has(c.valeur)) return false;
        vus.add(c.valeur);
        return true;
    });
    garde(disOk.length === 3, 'QCM : distracteurs en collision');
    const payload = makeQcm([bonne, ...disOk.map((c) => c.valeur)], bonne, texte, opts(L1), false);
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, unite: u.aire, distracteurs: disOk };
    return payload;
};

// ─── Format 2 : Vrai/Faux (pool fermé étiqueté à la main) ─────────────────

const AFFIRMATIONS = [
    { t: '1 cm² est l\'aire d\'un carré de 1 cm de côté.', v: true },
    { t: '1 dm² est l\'aire d\'un carré de 1 dm de côté.', v: true },
    { t: '1 m² est l\'aire d\'un carré de 1 m de côté.', v: true },
    { t: 'Le petit 2 de « cm² » se lit « carré » et rappelle un carré de 1 cm de côté.', v: true },
    { t: 'L\'unité d\'aire est un carré dont le côté mesure 1 unité de longueur.', v: true },
    { t: '1 m² est l\'aire d\'un carré de 10 cm de côté.', v: false },
    { t: '1 dm² est l\'aire d\'un carré de 2 dm de côté.', v: false },
    { t: '« cm² » se lit « centimètre deux ».', v: false },
    { t: '1 dm et 1 dm² désignent la même chose.', v: false },
    { t: 'Un carré de 1 m de côté a une aire de 1 dm².', v: false },
];

const genVraiFaux = () => {
    for (let essai = 0; essai < 30; essai++) {
        const tirage = shuffle([...AFFIRMATIONS]).slice(0, 4);
        const nbVraies = tirage.filter((a) => a.v).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            tirage.map((a) => a.t),
            tirage.map((a) => (a.v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        AFFIRMATIONS.slice(0, 2).concat(AFFIRMATIONS.slice(5, 7)).map((a) => a.t),
        [0, 0, 1, 1],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 3 : la phrase-lien à trous (drag) ─────────────────────────────

const genDrag = () => {
    const variante = randInt(1, 4);
    let phrase;
    let distracteurs;
    if (variante === 4) {
        const [u1, u2] = shuffle([...UNITES]).slice(0, 2);
        phrase = `1 {{${u1.aire}}}, c'est l'aire d'un carré de 1 ${u1.lon} de côté ; 1 {{${u2.aire}}}, c'est l'aire d'un carré de 1 ${u2.lon} de côté`;
        const troisieme = UNITES.find((x) => x !== u1 && x !== u2);
        distracteurs = [troisieme.aire, u1.lon];
    } else if (variante <= 2) {
        // deux unités parmi trois, dans les deux sens d'écriture
        const [u1, u2] = shuffle([...UNITES]).slice(0, 2);
        phrase = variante === 1
            ? `L'aire d'un carré de 1 ${u1.lon} de côté est 1 {{${u1.aire}}} ; l'aire d'un carré de 1 ${u2.lon} de côté est 1 {{${u2.aire}}}`
            : `Le carré de 1 ${u1.lon} de côté a une aire de 1 {{${u1.aire}}} ; le carré de 1 ${u2.lon} de côté a une aire de 1 {{${u2.aire}}}`;
        const troisieme = UNITES.find((x) => x !== u1 && x !== u2);
        distracteurs = [troisieme.aire, u1.lon];         // l'unité restante + la longueur SANS carré
    } else {
        // la lecture : le petit 2 se lit « carré » et rappelle la forme
        const u = randomChoice(UNITES);
        phrase = `L'écriture ${u.aire} se lit « ${u.nom.split(' ')[0]} {{carré}} » : le petit 2 rappelle un {{carré}} de 1 ${u.lon} de côté`;
        distracteurs = ['deux', 'rectangle'];
    }
    const disOk = filtrerDistracteurs(phrase, distracteurs);
    garde(disOk.length >= 2, 'drag : distracteurs filtrés');
    return makeTexteTrou(
        phrase,
        '<p><b>Complète avec les bonnes étiquettes.</b></p>',
        { ...opts(L1), mode: 'drag', distracteurs: disOk },
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'domaine FERMÉ (3 unités, une phrase rituelle) : variété par sous-types et habillages, assumée',
        'LE distracteur transversal : l\'unité de longueur SANS le carré (1 dm vs 1 dm²) — la confusion longueur / aire',
        'le « choix d\'unité adaptée » de la flash est écarté (autre automatisme)',
    ],
    unites_qcm_unique: {
        support: '3 sous-types : définition directe, définition inverse, lecture de la notation',
        fixes: {
            unite_voisine: 'une autre unité du domaine',
            sans_carre: 'l\'unité de longueur sans le ² (1 dm au lieu de 1 dm²)',
            carre_double: 'un carré de 2 unités de côté (le ² lu comme un doublement)',
            pas_un_carre: 'un rectangle 1 × 2 (l\'unité d\'aire n\'est pas un carré)',
            lecture_chiffre: '« centimètre deux » / « deux centimètres »',
            confusion_outil: '« double décimètre » (l\'instrument, pas l\'unité)',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    unites_qcm_unique: genQcm,
    unites_tableau_vf: genVraiFaux,
    unites_texte_trou_drag: genDrag,
};

const FORMAT_LABELS = {
    unites_qcm_unique: 'QCM',
    unites_tableau_vf: 'Vrai/Faux',
    unites_texte_trou_drag: 'Drag',
};

export const CATEGORIES = [
    {
        id: 'unitesaire',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
